/**
 * Envelope + error handling for the frozen API contract (docs/05-dev-spec.md §4).
 *
 * Deliberately framework-free (no `fetch`, no Vite globals) so it is unit-testable
 * in the `unit` Playwright project, which runs in plain Node.
 *
 * Two success shapes are ratified at T1.0 and both must unwrap:
 *   - the §4 convention        `{ "data": <payload>, "meta"?: { page, pageSize, total } }`
 *   - the named-list envelopes `{ applications: […], meta? }`, `{ items, meta }`,
 *                              `{ users, meta }` (README + T1.0 ratification note)
 * Anything that is neither is handed through untouched (single-object payloads,
 * file streams, `204 No Content` → `undefined`).
 *
 * Errors are always `{ "error": { "code": "STRING_CODE", "message": "…" } }`; the
 * legacy NestJS body (`{ message: string | string[] }`) is accepted as a fallback
 * because the global ValidationPipe answers that shape before any interceptor runs.
 */

/** `meta` block of a paginated envelope. The contract says `pageSize`; the
 *  shared `PageMeta` helper (and the list endpoints that reuse it) says `limit`
 *  / `totalPages` — both are optional here so neither shape throws. */
export interface EnvelopeMeta {
  page: number;
  pageSize?: number;
  limit?: number;
  total: number;
  totalPages?: number;
}

/** A successfully unwrapped payload plus its optional pagination block. */
export interface Envelope<T> {
  data: T;
  meta?: EnvelopeMeta;
}

/** Every documented error code (docs/05 §4) with its HTTP mapping. The wave-9
 *  additions travel the same wire shape, so they are not enumerated here: the
 *  server's own string is the authority and this union only names the codes the
 *  SPA itself acts on. */
export type ApiErrorCode =
  | 'VALIDATION'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'DUPLICATE_SNAPSHOT'
  | 'INTERNAL'
  | (string & {});

/** Structured payload the v1.8.0 codes may carry (`TOO_MANY_REQUESTS` →
 *  `retryAfterSeconds`, `PREFILL_FIO_INVALID` → `hint`, `PREFILL_QUOTA_EXCEEDED` →
 *  `limit`). Ids, counts and short strings only — never a person. */
export type ApiErrorMeta = Record<string, string | number | boolean>;

/** Body of an error response, normalised. */
export interface ApiErrorBody {
  code: ApiErrorCode;
  message: string;
  /** ADDITIVE (v1.8.0) — present only when the api sent `error.meta`. */
  meta?: ApiErrorMeta;
}

/** Envelope keys that may carry the payload when there is no `data` key. */
export const ENVELOPE_DATA_KEYS = ['data', 'applications', 'venues', 'items', 'users'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** True when the value looks like a pagination block (and not a payload). */
function looksLikeMeta(value: unknown): value is EnvelopeMeta {
  return isRecord(value) && typeof value.page === 'number' && typeof value.total === 'number';
}

/**
 * Unwraps a response body into `{ data, meta? }`.
 *
 * `keys` is the ordered list of payload keys to try (default: `data`, then the
 * named list envelopes). A body that carries none of them is returned as-is —
 * never `undefined`, so a caller cannot mistake "shape we did not expect" for
 * "empty list".
 */
export function unwrapEnvelope<T>(
  body: unknown,
  keys: readonly string[] = ENVELOPE_DATA_KEYS,
): Envelope<T> {
  if (!isRecord(body)) return { data: body as T };

  for (const key of keys) {
    if (key in body) {
      return { data: body[key] as T, meta: looksLikeMeta(body.meta) ? body.meta : undefined };
    }
  }
  return { data: body as T, meta: looksLikeMeta(body.meta) ? body.meta : undefined };
}

/** HTTP status → documented error code (docs/05 §4 table). */
export function statusCodeToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'VALIDATION';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'DUPLICATE_SNAPSHOT';
    default:
      return 'INTERNAL';
  }
}

/**
 * Normalises any error body into `{ code, message }`.
 *
 * `status` only fills the code in when the body does not carry one — the server
 * is always the authority on *why* a request failed.
 */
export function parseErrorBody(body: unknown, status: number): ApiErrorBody {
  let code: ApiErrorCode = statusCodeToCode(status);
  let message = '';
  let meta: ApiErrorMeta | undefined;

  if (isRecord(body) && isRecord(body.error)) {
    const error = body.error;
    if (typeof error.code === 'string') code = error.code as ApiErrorCode;
    if (typeof error.message === 'string') message = error.message;
    // ADDITIVE (v1.8.0): `error.meta` rides along when the code carries a
    // structured payload. Only flat string/number/boolean values are kept — a
    // nested object here is a shape we did not agree on, not something to render.
    if (isRecord(error.meta)) {
      const clean: ApiErrorMeta = {};
      for (const [key, value] of Object.entries(error.meta)) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          clean[key] = value;
        }
      }
      if (Object.keys(clean).length > 0) meta = clean;
    }
  } else if (isRecord(body)) {
    if (Array.isArray(body.message)) message = body.message.map(String).join('; ');
    else if (typeof body.message === 'string') message = body.message;
    else if (typeof body.error === 'string') message = body.error;
  }

  // The last resort, when neither the contract envelope nor the legacy Nest
  // body is recognisable: something readable has to reach the ErrorState.
  if (!message) {
    try {
      message = body === undefined ? 'пустой ответ' : JSON.stringify(body) || 'пустой ответ';
    } catch {
      message = 'не удалось разобрать тело ответа';
    }
  }
  return meta === undefined ? { code, message } : { code, message, meta };
}
